import { useMemo, useState } from 'react';
import { useNostr } from '@nostrify/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { useBCHPrice } from '@/hooks/useBCHPrice';
import {
  DIRECT_LISTING_CATEGORIES,
  generateBusinessIdentity,
  buildProfileContent,
  buildListingTemplate,
  buildEncryptedKeyBackup,
  signWithKey,
  type DirectListingDraft,
  type GeneratedIdentity,
} from '@/lib/directListing';
import {
  PLATFORM_FEE_ADDRESS,
  verifyFeeSats,
  auditClaim,
} from '@/lib/businessVerify';
import { toast } from '@/hooks/useToast';
import {
  Store,
  Copy,
  Check,
  Loader2,
  CircleCheck,
  ShieldAlert,
  KeyRound,
} from 'lucide-react';

interface ListBusinessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** One-time fee to list a business directly (USD). Also grants verification. */
const LISTING_FEE_USD = 10;
const TXID_RE = /^[0-9a-f]{64}$/i;

function bip21(address: string, bchAmount: number): string {
  const addr = address.replace(/^bitcoincash:/i, '');
  const amount = bchAmount > 0
    ? `?amount=${bchAmount.toFixed(8).replace(/0+$/, '').replace(/\.$/, '')}`
    : '';
  return `bitcoincash:${addr}${amount}`;
}

type Step = 'form' | 'pay' | 'done';

export function ListBusinessDialog({ open, onOpenChange }: ListBusinessDialogProps) {
  const { nostr } = useNostr();
  const { data: price, isLoading: priceLoading } = useBCHPrice();

  const [step, setStep] = useState<Step>('form');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [txid, setTxid] = useState('');
  const [identity, setIdentity] = useState<GeneratedIdentity | null>(null);
  const [savedKey, setSavedKey] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState('other');
  const [tagline, setTagline] = useState('');
  const [bch, setBch] = useState('');
  const [website, setWebsite] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [location, setLocation] = useState('');

  const feeSats = useMemo(() => verifyFeeSats(LISTING_FEE_USD, price ?? null), [price]);
  const bchAmount = feeSats / 1e8;
  const uri = useMemo(() => bip21(PLATFORM_FEE_ADDRESS, bchAmount), [bchAmount]);
  const bareAddress = PLATFORM_FEE_ADDRESS.replace(/^bitcoincash:/i, '');

  const reset = () => {
    setStep('form');
    setBusy(false);
    setError('');
    setTxid('');
    setIdentity(null);
    setSavedKey(false);
    setName('');
    setCategory('other');
    setTagline('');
    setBch('');
    setWebsite('');
    setOnlineOnly(false);
    setLocation('');
  };

  const close = (o: boolean) => {
    if (busy) return;
    if (!o) reset();
    onOpenChange(o);
  };

  const copy = async (text: string, which: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  const draft: DirectListingDraft = {
    name,
    category,
    tagline,
    bch,
    website,
    onlineOnly,
    location,
  };

  const handleContinue = () => {
    setError('');
    if (!name.trim()) {
      setError('Please enter your business name.');
      return;
    }
    if (!onlineOnly && !location.trim()) {
      setError('Add a location, or mark the business as online-only.');
      return;
    }
    setStep('pay');
  };

  const handleSubmit = async () => {
    setError('');
    const clean = txid.trim().toLowerCase();
    if (!TXID_RE.test(clean)) {
      setError('Enter a valid 64-character transaction ID (txid).');
      return;
    }

    setBusy(true);
    try {
      // 1) Confirm the payment on-chain.
      const audit = await auditClaim(clean, feeSats);
      if (audit.verdict === 'wrong-recipient') {
        throw new Error('That transaction did not pay the listing address.');
      }
      if (audit.verdict === 'underpaid') {
        throw new Error('That payment was less than the listing fee.');
      }
      if (audit.verdict === 'not-found' || audit.verdict === 'no-record') {
        throw new Error('Could not find that transaction yet. Wait for it to broadcast, then try again.');
      }

      // 2) Generate a managed identity and publish the profile + listing.
      const id = generateBusinessIdentity();
      const profile = signWithKey(
        { kind: 0, content: buildProfileContent(draft), tags: [], created_at: Math.floor(Date.now() / 1000) },
        id.secretKey,
      );
      const listingTemplate = buildListingTemplate(draft);
      const listing = signWithKey(listingTemplate, id.secretKey);

      // Encrypted key backup — only the admin can decrypt this.
      const keyBackup = buildEncryptedKeyBackup(id);

      await Promise.all([
        nostr.event(profile, { signal: AbortSignal.timeout(5000) }),
        nostr.event(listing, { signal: AbortSignal.timeout(5000) }),
        nostr.event(keyBackup, { signal: AbortSignal.timeout(5000) }),
      ]);

      setIdentity(id);
      setStep('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="size-5 text-bch" />
            List your business on BCH Local
          </DialogTitle>
          <DialogDescription>
            {step === 'form' && 'No account needed. Fill in your details, pay a one-time $10 fee in BCH, and get listed + verified.'}
            {step === 'pay' && `Pay the one-time $${LISTING_FEE_USD} listing fee, then paste your transaction to submit.`}
            {step === 'done' && 'Your listing has been submitted.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'form' && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="biz-name">Business name *</Label>
              <Input id="biz-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Coffee" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="biz-category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="biz-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIRECT_LISTING_CATEGORIES.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="biz-tagline">Short description</Label>
              <Textarea
                id="biz-tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="Specialty coffee & pastries, BCH accepted."
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border px-3 py-2">
              <div>
                <Label htmlFor="biz-online" className="text-sm">Online only</Label>
                <p className="text-xs text-muted-foreground">No physical location</p>
              </div>
              <Switch id="biz-online" checked={onlineOnly} onCheckedChange={setOnlineOnly} />
            </div>

            {!onlineOnly && (
              <div className="space-y-1.5">
                <Label htmlFor="biz-location">Location *</Label>
                <Input
                  id="biz-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Austin, TX"
                />
                <p className="text-[11px] text-muted-foreground">
                  City & region are used to place you on the map.
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="biz-bch">BCH address (optional)</Label>
              <Input id="biz-bch" value={bch} onChange={(e) => setBch(e.target.value)} placeholder="bitcoincash:q…" className="font-mono text-xs" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="biz-website">Website (optional)</Label>
              <Input id="biz-website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" />
            </div>

            {error && <p className="text-xs text-destructive">{error}</p>}

            <Button className="w-full bg-bch hover:bg-bch/90 text-bch-foreground" onClick={handleContinue}>
              Continue to payment
            </Button>
          </div>
        )}

        {step === 'pay' && (
          <div className="space-y-4">
            <Alert className="border-bch/30 bg-bch/5">
              <CircleCheck className="size-4 text-bch" />
              <AlertTitle>One fee, listed + verified</AlertTitle>
              <AlertDescription className="text-muted-foreground">
                Your ${LISTING_FEE_USD} payment lists <strong className="text-foreground">{name}</strong> and
                grants a verified badge once approved.
              </AlertDescription>
            </Alert>

            <div className="flex flex-col items-center gap-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-bch">${LISTING_FEE_USD}</div>
                <div className="text-xs text-muted-foreground">
                  {priceLoading || feeSats <= 0
                    ? 'Fetching BCH price…'
                    : `≈ ${bchAmount.toFixed(8).replace(/0+$/, '').replace(/\.$/, '')} BCH · one-time`}
                </div>
              </div>

              <div className="rounded-xl bg-white p-3">
                <QRCodeCanvas value={uri} size={170} />
              </div>

              <div className="w-full space-y-2">
                <div className="flex items-center gap-2">
                  <Input readOnly value={bareAddress} className="font-mono text-xs" />
                  <Button variant="outline" size="icon" className="shrink-0" onClick={() => copy(bareAddress, 'addr')} aria-label="Copy address">
                    {copied === 'addr' ? <Check className="size-4 text-bch" /> : <Copy className="size-4" />}
                  </Button>
                </div>
                <Button variant="secondary" className="w-full" onClick={() => copy(uri, 'uri')} disabled={feeSats <= 0}>
                  {copied === 'uri' ? <Check className="size-4 mr-2 text-bch" /> : <Copy className="size-4 mr-2" />}
                  Copy payment link
                </Button>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="biz-txid">After paying, paste your transaction ID</Label>
              <Input
                id="biz-txid"
                value={txid}
                onChange={(e) => setTxid(e.target.value)}
                placeholder="64-character txid"
                className="font-mono text-xs"
                disabled={busy}
              />
              {error && <p className="text-xs text-destructive">{error}</p>}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setStep('form')} disabled={busy}>
                  Back
                </Button>
                <Button
                  className="flex-1 bg-bch hover:bg-bch/90 text-bch-foreground"
                  onClick={handleSubmit}
                  disabled={busy || !txid.trim()}
                >
                  {busy && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {busy ? 'Submitting…' : 'Submit listing'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 'done' && identity && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-2 text-center py-2">
              <CircleCheck className="size-10 text-bch" />
              <p className="font-semibold">Listing submitted 🎉</p>
              <p className="text-sm text-muted-foreground">
                {name} will appear on the map once an admin approves it (usually within 24 hours).
                Your verified badge is granted at the same time.
              </p>
            </div>

            <Alert variant="destructive" className="border-amber-500/40 bg-amber-500/5 text-foreground">
              <KeyRound className="size-4 text-amber-600 dark:text-amber-400" />
              <AlertTitle className="text-amber-700 dark:text-amber-400">Save your recovery key</AlertTitle>
              <AlertDescription className="text-muted-foreground">
                This secret key controls your listing. Save it somewhere safe if you want to manage
                the listing yourself later. If you lose it, contact us and we'll help.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label className="text-xs">Secret key (nsec)</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={identity.nsec} className="font-mono text-xs" />
                <Button variant="outline" size="icon" className="shrink-0" onClick={() => copy(identity.nsec, 'nsec')} aria-label="Copy nsec">
                  {copied === 'nsec' ? <Check className="size-4 text-bch" /> : <Copy className="size-4" />}
                </Button>
              </div>
              <label className="flex items-start gap-2 text-xs text-muted-foreground pt-1">
                <input type="checkbox" checked={savedKey} onChange={(e) => setSavedKey(e.target.checked)} className="mt-0.5" />
                <span className="flex items-center gap-1">
                  <ShieldAlert className="size-3 text-amber-500 shrink-0" />
                  I've saved my recovery key somewhere safe.
                </span>
              </label>
            </div>

            <Button className="w-full bg-bch hover:bg-bch/90 text-bch-foreground" disabled={!savedKey} onClick={() => close(false)}>
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
